import React, { useMemo, useState } from "react";
import {
  Box, Stack, Typography, Button, IconButton, Tooltip,
  Collapse, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel,
} from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InventoryIcon from "@mui/icons-material/Inventory";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import RestoreIcon from "@mui/icons-material/Restore";
import LinkIcon from "@mui/icons-material/Link";
import LeadNumberBadge from "./LeadNumberBadge";
import { STATUS_CONFIG, type LeadThread } from "./leadApi";
import { leadConfig, showsVenueField, resolveInquirySubject, duplicateWorkflowLabel } from "./leadVertical";

/* ─────────────────────────────────────────────────────────────────────────
 * Centralised semantic palette.
 * ───────────────────────────────────────────────────────────────────────── */
const TOKEN = {
  navy:     "#16233A",
  navySoft: "#1e3a5f",
  gold:     "#7C4DFF",
  goldDeep: "#b45309",
  goldBg:   "#fef3c7",
  goldEdge: "#fde68a",
  primaryEventEdge: "#fcd34d",
  active:   "#3b82f6",
  related:  "#6d28d9",
  relatedEdge: "#ddd6fe",
  relatedBg: "#ede9fe",
  archived: "#94a3b8",
  archivedBg: "#f1f5f9",
  archivedEdge: "#e2e8f0",
  danger:   "#dc2626",
  dangerBg: "#fef2f2",
  dangerEdge: "#fecaca",
  success:  "#16a34a",
  successBg: "#dcfce7",
  successEdge: "#bbf7d0",
  border:   "#e5e7eb",
};

/* Predefined same-event reasons — stored as text in archived_reason. */
const SAME_EVENT_REASONS = [
  { value: "Duplicate Web Inquiry", color: TOKEN.related },
  { value: "Quote Comparison",       color: "#0891b2"     },
  { value: "Family Inquiry",         color: "#0d9488"     },
  { value: "Internal Duplicate",     color: TOKEN.archived },
  { value: "Spam/Test",              color: TOKEN.danger   },
  { value: "Other",                  color: TOKEN.navy     },
];

interface Props {
  currentLead: LeadThread;
  relatedLeads: LeadThread[];
  canManageResolution: boolean;
  density?: "compact" | "comfortable";
  onNavigateLead: (id: number) => void;
  /** Operate on a specific lead — every card now passes its own id. */
  onMarkPrimary:    (leadId: number) => void | Promise<void>;
  onFlagSameEvent:  (childLeadId: number, primaryLeadId: number, reason?: string) => void | Promise<void>;
  onTrash:          (leadId: number) => void | Promise<void>;
  onRestore:        (leadId: number) => void | Promise<void>;
  onOpenCustomer?:  (customerId: number) => void;
}

/**
 * Customer-centric relationship panel — per-card contextual actions.
 *
 *   1. Customer summary  — stat strip
 *   2. Primary event     — dominant gold card (with Trash action only)
 *   3. Related enquiries — each card has Set Primary / Flag Same Event / Trash
 *   4. Archived / same   — collapsed; each row has Restore
 *   5. (Removed)         — no global toolbar; ambiguity is gone
 *
 * Reason dialog uses predefined chips so the operator can flag in <2s.
 * Trash dialog asks for confirmation. Auto-reorder happens via parent
 * refetch — primary always re-sorts to the top after Set Primary.
 */
export default function CustomerRelationshipPanel({
  currentLead, relatedLeads, canManageResolution, density = "comfortable",
  onNavigateLead, onMarkPrimary, onFlagSameEvent, onTrash, onRestore, onOpenCustomer,
}: Props) {
  const compact = density === "compact";

  /* ── Derivations ────────────────────────────────────────────────────── */
  // Defensive: backend already excludes the current lead from /related,
  // but if a stale cache or future regression leaks it through, dedupe by
  // id so the current lead can never appear twice in `allLeads`.
  const allLeads = useMemo(
    () => [currentLead, ...relatedLeads.filter((l) => l.id !== currentLead.id)],
    [currentLead, relatedLeads],
  );

  const active   = useMemo(() => allLeads.filter((l) => !l.archived_at && !l.merged_into_lead_id), [allLeads]);
  const archived = useMemo(() => allLeads.filter((l) =>  l.archived_at ||  l.merged_into_lead_id), [allLeads]);

  // Primary preference: explicit is_primary_event flag in the active set.
  const primary = useMemo(() => active.find((l) => l.is_primary_event) ?? null, [active]);
  // "Related Enquiries" lists OTHER active leads only — never the lead
  // the operator is currently viewing, and never the primary (which has
  // its own dominant card above). Without the currentLead exclusion the
  // current lead would self-reference whenever no primary is set.
  const otherActive = useMemo(
    () => active.filter((l) => l.id !== currentLead.id && l.id !== primary?.id),
    [active, currentLead.id, primary],
  );

  // (Stat-strip derivations removed along with Section 1 — those
  //  metrics now live exclusively in the top-of-page KPI cards row.)

  /* ── Per-card action dialogs ────────────────────────────────────────── */
  const [flagDialog,  setFlagDialog]  = useState<null | { childId: number }>(null);
  const [flagReason,  setFlagReason]  = useState<string>("");
  const [flagNotes,   setFlagNotes]   = useState<string>("");
  // When >1 active candidate primary, the operator picks which one.
  const [flagPrimary, setFlagPrimary] = useState<number | "">("");

  const [trashDialog, setTrashDialog] = useState<null | { leadId: number; leadNumber: string | null; isPrimary: boolean }>(null);
  // Primary-event guard — operator must explicitly acknowledge they're
  // trashing THE primary booking lead (not just any related enquiry).
  // Reset every time a new trash dialog opens so a previous tick can't
  // carry over to a different lead.
  const [primaryTrashAck, setPrimaryTrashAck] = useState(false);

  const openFlagDialog = (childId: number) => {
    // Auto-default the primary: explicit one wins, else the first active sibling.
    const defaultPrimary = primary
      ? primary.id
      : active.find((l) => l.id !== childId)?.id ?? "";
    setFlagPrimary(defaultPrimary);
    setFlagReason("Duplicate Web Inquiry");
    setFlagNotes("");
    setFlagDialog({ childId });
  };

  /* ── Render ─────────────────────────────────────────────────────────── */
  return (
    <Box sx={{
      bgcolor: "#fff",
      border: "1px solid #eef0f4",
      borderRadius: "14px",
      overflow: "hidden",
      boxShadow: "0 1px 2px rgba(22,35,58,0.04), 0 4px 16px rgba(22,35,58,0.04)",
    }}>
      {/* ════ SECTION 1 removed — the dark navy "Inquiry Contact +
          Linked Customer + stat pills" panel was visually duplicating
          information that already lives in the hero header (inquiry +
          linked customer) and the KPI stat cards row (Total / Active /
          Archived / Same Event / Quotes / Bookings). Customer profile
          access remains available from the hero's "Linked Customer" pill
          and the Contact Summary Strip's "Customer Profile" button. */}

      {/* ════ SECTION 2 · PRIMARY EVENT (dominant gold card) ══════════ */}
      {primary && (
        <Box sx={{ px: compact ? 1.6 : 2, py: compact ? 1 : 1.3 }}>
          <SectionLabel
            label="Primary Event"
            color={TOKEN.goldDeep}
            accent={<StarRoundedIcon sx={{ fontSize: 14, color: TOKEN.goldDeep }} />}
          />
          <PrimaryEventCard
            lead={primary}
            isCurrent={primary.id === currentLead.id}
            canManage={canManageResolution}
            onClick={() => onNavigateLead(primary.id)}
            onTrash={() => { setPrimaryTrashAck(false); setTrashDialog({ leadId: primary.id, leadNumber: primary.lead_number ?? null, isPrimary: true }); }}
          />
        </Box>
      )}
      {/* Legacy primary-event banner retired from Step Up UI. The
          "primary event" concept was a DCC event-booking artefact
          and has no meaning in an education-enrolment funnel; the
          underlying is_primary_event column + backend actions are
          retained (admins can still mark a primary via the More
          menu) but the always-visible empty-state prompt is gone
          because it advertised a workflow this tenant does not use. */}

      {/* ════ SECTION 3 · RELATED ENQUIRIES (each card has its own actions) */}
      {otherActive.length > 0 && (
        <Box sx={{ px: compact ? 1.6 : 2, pb: 1.5 }}>
          <SectionLabel
            label={`Related Enquiries (${otherActive.length})`}
            color={TOKEN.related}
            accent={<LinkIcon sx={{ fontSize: 13, color: TOKEN.related }} />}
          />
          <Stack gap={0.8}>
            {otherActive.map((l) => (
              <RelatedLeadCard
                key={l.id}
                lead={l}
                currentLead={currentLead}
                canManage={canManageResolution}
                onClick={() => onNavigateLead(l.id)}
                onSetPrimary={() => onMarkPrimary(l.id)}
                onFlagSameEvent={() => openFlagDialog(l.id)}
                onTrash={() => { setPrimaryTrashAck(false); setTrashDialog({ leadId: l.id, leadNumber: l.lead_number ?? null, isPrimary: false }); }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* ════ SECTION 4 · ARCHIVED / SAME EVENT (collapsed; inline restore) */}
      {archived.length > 0 && (
        <ArchivedSection
          archived={archived}
          currentLead={currentLead}
          canManage={canManageResolution}
          compact={compact}
          onNavigate={onNavigateLead}
          onRestore={onRestore}
        />
      )}

      {/* ── Flag Same Event dialog (reason chips + auto primary) ─────── */}
      <Dialog open={!!flagDialog} onClose={() => setFlagDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: TOKEN.navy, fontSize: 16 }}>
          Flag as same event
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.2, fontSize: 11.5 }}>
            This enquiry will be hidden from active pipeline as a <i>same-event duplicate</i>. History is preserved and the lead can be restored later.
          </Typography>

          {/* Primary picker — auto-defaults; only shown when ambiguous */}
          {active.length > 1 && (
            <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
              <InputLabel>Primary lead</InputLabel>
              <Select
                label="Primary lead"
                value={flagPrimary === "" ? "" : String(flagPrimary)}
                onChange={(e) => setFlagPrimary(Number(e.target.value))}
              >
                {active
                  .filter((l) => l.id !== flagDialog?.childId)
                  .map((l) => (
                    <MenuItem key={l.id} value={String(l.id)}>
                      <Stack direction="row" alignItems="center" gap={0.7}>
                        {l.is_primary_event && <StarRoundedIcon sx={{ fontSize: 14, color: TOKEN.goldDeep }} />}
                        <Typography variant="caption" sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 11 }}>
                          {l.lead_number ?? `#${l.id}`}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: 11, textTransform: "capitalize" }}>
                          {(() => {
                            const s = resolveInquirySubject(l);
                            const bits: string[] = [];
                            if (leadConfig.supportsEventFields) {
                              bits.push(l.event_type?.replace(/_/g, " ") ?? "—");
                              if (showsVenueField("event_date")) bits.push(l.event_date ?? "no date");
                            } else if (s.program) {
                              bits.push(s.program);
                            }
                            return bits.length ? ` · ${bits.join(" · ")}` : "";
                          })()}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}

          {/* Reason chips */}
          <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10.5, color: TOKEN.related, letterSpacing: 0.5, textTransform: "uppercase", display: "block", mb: 0.6 }}>
            Reason
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 0.6, mb: 1.3 }}>
            {SAME_EVENT_REASONS.map((r) => {
              const active = flagReason === r.value;
              return (
                <Box
                  key={r.value}
                  onClick={() => setFlagReason(r.value)}
                  sx={{
                    cursor: "pointer", userSelect: "none",
                    p: 0.7, borderRadius: 1.2,
                    bgcolor: active ? `${r.color}14` : "#fff",
                    border: `1.5px solid ${active ? r.color : TOKEN.border}`,
                    color: active ? r.color : "#475569",
                    transition: "all 0.15s",
                    "&:hover": { borderColor: r.color, bgcolor: `${r.color}0A` },
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 11, lineHeight: 1.2 }}>{r.value}</Typography>
                </Box>
              );
            })}
          </Box>

          <TextField
            size="small" fullWidth multiline rows={2}
            label="Additional notes (optional)"
            value={flagNotes}
            onChange={(e) => setFlagNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFlagDialog(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained" disabled={!flagPrimary || !flagReason}
            onClick={async () => {
              if (!flagDialog || typeof flagPrimary !== "number") return;
              const compiledReason = flagNotes
                ? `${flagReason} — ${flagNotes}`
                : flagReason;
              await onFlagSameEvent(flagDialog.childId, flagPrimary, compiledReason);
              setFlagDialog(null);
            }}
            sx={{ textTransform: "none", bgcolor: TOKEN.related, "&:hover": { bgcolor: "#5b21b6" } }}
          >
            Flag Same Event
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Trash confirmation ──────────────────────────────────────────
          Primary-event variant adds a yellow warning callout + explicit
          "I understand this is the primary booking lead" checkbox the
          operator MUST tick before the destructive button enables. The
          primary card is the canonical event-of-record on a customer, so
          trashing it accidentally loses the gold-bordered booking pin and
          forces a re-pick — well worth a second confirmation. */}
      <Dialog open={!!trashDialog} onClose={() => setTrashDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: TOKEN.navy, fontSize: 16 }}>
          {trashDialog?.isPrimary ? "Trash the PRIMARY event?" : "Move lead to trash?"}
        </DialogTitle>
        <DialogContent>
          {trashDialog?.isPrimary && (
            <Box sx={{
              display: "flex", alignItems: "flex-start", gap: 1,
              p: 1.2, mb: 1.5, borderRadius: 1.5,
              bgcolor: TOKEN.goldBg, border: `1px solid ${TOKEN.goldEdge}`,
            }}>
              <WarningAmberRoundedIcon sx={{ fontSize: 18, color: TOKEN.goldDeep, mt: "1px" }} />
              <Typography variant="caption" sx={{ fontSize: 11.5, color: TOKEN.goldDeep, fontWeight: 700, lineHeight: 1.4 }}>
                This is the <b>Primary Event</b> for this customer. Trashing it
                removes the gold booking pin — another enquiry will need to be
                promoted with <b>Set Primary</b>.
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12.5 }}>
            Lead <b>{trashDialog?.leadNumber ?? `#${trashDialog?.leadId}`}</b> will be hidden from active pipeline. <b>Nothing is deleted</b> — open follow-ups are cancelled and the lead remains in customer history. It can be restored at any time.
          </Typography>

          {trashDialog?.isPrimary && (
            <FormControlLabel
              sx={{ mt: 1.5, alignItems: "flex-start", "& .MuiFormControlLabel-label": { fontSize: 12, color: TOKEN.navy, lineHeight: 1.4, pt: 0.4 } }}
              control={
                <Checkbox
                  size="small"
                  checked={primaryTrashAck}
                  onChange={(e) => setPrimaryTrashAck(e.target.checked)}
                  sx={{ py: 0, color: TOKEN.danger, "&.Mui-checked": { color: TOKEN.danger } }}
                />
              }
              label={<>I understand this is the <b>primary booking lead</b> for this customer.</>}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTrashDialog(null)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!!trashDialog?.isPrimary && !primaryTrashAck}
            onClick={async () => {
              if (!trashDialog) return;
              await onTrash(trashDialog.leadId);
              setTrashDialog(null);
            }}
            sx={{ textTransform: "none", bgcolor: TOKEN.danger, "&:hover": { bgcolor: "#b91c1c" } }}
          >
            {trashDialog?.isPrimary ? "Yes, trash primary event" : "Move to Trash"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ═════ Local sub-components ═══════════════════════════════════════════ */

const MONO = "'SF Mono','JetBrains Mono','Roboto Mono',Menlo,Consolas,monospace";

function SectionLabel({ label, color, accent }: { label: string; color: string; accent?: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" gap={0.6} mb={0.8}>
      {accent}
      <Typography variant="caption" sx={{
        fontSize: 10, fontWeight: 800, letterSpacing: 1.1, textTransform: "uppercase", color,
      }}>
        {label}
      </Typography>
    </Stack>
  );
}

/* StatPill removed with Section 1 — the page's top KPI cards row now
   owns those metrics. */

/* ── Primary event card — gold, dominant; Trash is its only destructive action.
   New layout: vertical content stack with a dedicated bottom row reserved
   for actions, so the meta line never has to fight the buttons for width. */
function PrimaryEventCard({
  lead, isCurrent, canManage, onClick, onTrash,
}: {
  lead: LeadThread; isCurrent: boolean; canManage: boolean;
  onClick: () => void; onTrash: () => void;
}) {
  const statusCfg = STATUS_CONFIG[lead.status] || { label: lead.status, color: "#94a3b8" };
  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer", borderRadius: 1.5,
        background: `linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)`,
        border: `2px solid ${TOKEN.primaryEventEdge}`,
        position: "relative",
        boxShadow: "0 4px 14px rgba(180,131,9,0.18)",
        transition: "all 0.18s",
        "&:hover": { transform: "translateY(-1px)", boxShadow: "0 6px 18px rgba(180,131,9,0.26)" },
      }}
    >
      <Box sx={{
        position: "absolute", top: 0, right: 0,
        bgcolor: TOKEN.goldDeep, color: "#fff",
        fontSize: 9, fontWeight: 800, letterSpacing: 0.6,
        px: 0.9, py: 0.2, borderRadius: "0 6px 0 8px",
      }}>
        ★ PRIMARY EVENT
      </Box>
      <Box sx={{ p: 1.3, pt: 1.7 }}>
        {/* Row 1 — identifiers */}
        <Stack direction="row" alignItems="center" gap={0.6} mb={0.5} flexWrap="wrap">
          <LeadNumberBadge value={lead.lead_number ?? `#${lead.id}`} variant="muted" />
          <Box sx={{
            fontSize: 9, fontWeight: 800, letterSpacing: 0.5,
            bgcolor: `${statusCfg.color}22`, color: statusCfg.color,
            border: `1px solid ${statusCfg.color}55`, borderRadius: 0.6,
            px: 0.6, py: 0.1, textTransform: "uppercase",
          }}>
            {statusCfg.label}
          </Box>
          {isCurrent && (
            <Box sx={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.5, bgcolor: "#16233A", color: "#fff", borderRadius: 0.6, px: 0.6, py: 0.1, textTransform: "uppercase" }}>
              Viewing
            </Box>
          )}
        </Stack>

        {/* Row 2 — inquiry meta line. Venue: event · date · guests.
            Autism / tutoring: program · location (from custom_fields). */}
        <Typography variant="body2" sx={{ fontWeight: 700, color: TOKEN.navy, fontSize: 13, textTransform: "capitalize" }}>
          {(() => {
            const s = resolveInquirySubject(lead);
            if (leadConfig.supportsEventFields) {
              return (
                <>
                  {lead.event_type?.replace(/_/g, " ") ?? "Event"}
                  {showsVenueField("event_date") && lead.event_date && ` · ${fmtEventDate(lead.event_date)}`}
                  {showsVenueField("guest_count") && lead.guest_count != null && ` · ${lead.guest_count} guests`}
                </>
              );
            }
            const parts = [s.program, s.location].filter(Boolean).join(" · ");
            return parts || "Inquiry";
          })()}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.3, fontSize: 11 }}>
          {lead.assignee
            ? <>Assigned to <b>{displayName(lead.assignee)}</b></>
            : "Unassigned"}
        </Typography>

        {/* Row 3 — actions; dedicated row, no-wrap, right-aligned. */}
        {canManage && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1, flexWrap: "nowrap" }}>
            <CardAction
              label="Trash" color={TOKEN.danger} bg={TOKEN.dangerBg} edge={TOKEN.dangerEdge}
              icon={<DeleteSweepIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => { e.stopPropagation(); onTrash(); }}
            />
          </Stack>
        )}
      </Box>
    </Box>
  );
}

/* ── Related-lead card — stacked rows; actions get their own no-wrap bottom row
   so the layout reads identically at 280px (drawer) or 360px+ (lead page).
   Match-reason badges sit above the action row, never compete for horizontal
   space with the buttons. */
function RelatedLeadCard({
  lead, currentLead, canManage, onClick, onSetPrimary, onFlagSameEvent, onTrash,
}: {
  lead: LeadThread; currentLead: LeadThread; canManage: boolean;
  onClick: () => void;
  onSetPrimary: () => void; onFlagSameEvent: () => void; onTrash: () => void;
}) {
  const statusCfg = STATUS_CONFIG[lead.status] || { label: lead.status, color: "#94a3b8" };
  const matchReasons = buildMatchReasons(lead, currentLead);
  const isCurrent = lead.id === currentLead.id;

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer", borderRadius: 1.5,
        bgcolor: "#fff",
        border: `1px solid ${TOKEN.border}`,
        borderLeft: `3px solid ${TOKEN.related}`,
        transition: "all 0.15s",
        "&:hover": { borderColor: TOKEN.related, boxShadow: "0 2px 8px rgba(22,35,58,0.06)" },
      }}
    >
      <Box sx={{ p: 1.1 }}>
        {/* Row 1 — identifiers + tags + relative time on the right */}
        <Stack direction="row" alignItems="center" gap={0.6} flexWrap="wrap">
          <LeadNumberBadge value={lead.lead_number ?? `#${lead.id}`} variant="muted" />
          <Box sx={{
            fontSize: 9, fontWeight: 800, letterSpacing: 0.5,
            bgcolor: `${statusCfg.color}22`, color: statusCfg.color,
            border: `1px solid ${statusCfg.color}55`, borderRadius: 0.6,
            px: 0.5, py: 0.05, textTransform: "uppercase",
          }}>
            {statusCfg.label}
          </Box>
          {isCurrent && (
            <Tooltip title="This is the lead you're viewing">
              <Box sx={{ fontSize: 9, fontWeight: 800, bgcolor: TOKEN.navy, color: "#fff", borderRadius: 0.6, px: 0.5, py: 0.05 }}>You</Box>
            </Tooltip>
          )}
          <Box flex={1} />
          <Tooltip title={fmtAbsolute(lead.created_at)}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, whiteSpace: "nowrap" }}>
              {fmtRelative(lead.created_at)}
            </Typography>
          </Tooltip>
        </Stack>

        {/* Row 2 — vertical-aware inquiry meta line. */}
        <Typography variant="body2" sx={{ fontWeight: 600, color: TOKEN.navy, fontSize: 12, mt: 0.4, textTransform: "capitalize" }}>
          {(() => {
            const s = resolveInquirySubject(lead);
            if (leadConfig.supportsEventFields) {
              return (
                <>
                  {lead.event_type?.replace(/_/g, " ") ?? "—"}
                  {showsVenueField("event_date") && lead.event_date && ` · ${fmtEventDate(lead.event_date)}`}
                  {showsVenueField("guest_count") && lead.guest_count != null && ` · ${lead.guest_count} guests`}
                </>
              );
            }
            return [s.program ?? "—", s.location].filter(Boolean).join(" · ");
          })()}
        </Typography>

        {/* Row 3 — assignee */}
        {lead.assignee && (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10.5, display: "block", mt: 0.15 }}>
            {displayName(lead.assignee)}
          </Typography>
        )}

        {/* Row 4 — match-reason chips with semantic icons */}
        {matchReasons.length > 0 && (
          <Stack direction="row" gap={0.4} mt={0.6} flexWrap="wrap">
            {matchReasons.map((m) => (
              <Box key={m} sx={{
                display: "inline-flex", alignItems: "center", gap: 0.25,
                fontSize: 8.5, fontWeight: 700, letterSpacing: 0.4,
                bgcolor: TOKEN.relatedBg, color: TOKEN.related,
                border: `1px solid ${TOKEN.relatedEdge}`,
                borderRadius: 0.6, px: 0.5, py: 0.05, textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}>
                <span style={{ fontSize: 9 }}>{matchIcon(m)}</span>{m}
              </Box>
            ))}
          </Stack>
        )}

        {/* Row 5 — compact inline action pills (reference design).
            Removed the `fullWidth` flag so each button is sized to its
            own content; right-aligned via marginLeft auto on the first
            button so they sit as a balanced trio without stretching
            edge-to-edge like form controls. */}
        {canManage && (
          <Stack
            direction="row" gap={0.6}
            sx={{ mt: 1, flexWrap: "nowrap", justifyContent: "flex-end" }}
          >
            <CardAction
              label="Set Primary" color={TOKEN.goldDeep} bg={TOKEN.goldBg} edge={TOKEN.goldEdge}
              icon={<StarRoundedIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => { e.stopPropagation(); onSetPrimary(); }}
            />
            <CardAction
              label={duplicateWorkflowLabel} color={TOKEN.related} bg={TOKEN.relatedBg} edge={TOKEN.relatedEdge}
              icon={<LinkIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => { e.stopPropagation(); onFlagSameEvent(); }}
            />
            <CardAction
              label="Trash" color={TOKEN.danger} bg={TOKEN.dangerBg} edge={TOKEN.dangerEdge}
              icon={<DeleteSweepIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => { e.stopPropagation(); onTrash(); }}
            />
          </Stack>
        )}
      </Box>
    </Box>
  );
}

/* Semantic icon for match-reason chips. Inline emoji keeps the chip
   compact without pulling in another MUI icon at this small size. */
function matchIcon(reason: string): string {
  if (reason === "Same phone")      return "📞";
  if (reason === "Same email")      return "✉";
  if (reason === "Same event date") return "📅";
  if (reason === "Same event type") return "🎯";
  if (reason === "Same customer")   return "👤";
  return "•";
}

/* ── Archived/same-event accordion with inline Restore. */
function ArchivedSection({
  archived, currentLead, canManage, compact, onNavigate, onRestore,
}: {
  archived: LeadThread[]; currentLead: LeadThread; canManage: boolean; compact: boolean;
  onNavigate: (id: number) => void;
  onRestore: (id: number) => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);

  const sameEvent     = archived.filter((l) => l.resolution_status === "same_event_duplicate");
  const otherArchived = archived.filter((l) => l.resolution_status !== "same_event_duplicate");

  return (
    <Box sx={{ borderTop: `1px solid ${TOKEN.border}`, bgcolor: "#fafafa" }}>
      <Stack
        direction="row" alignItems="center" gap={1}
        onClick={() => setOpen((p) => !p)}
        sx={{ px: compact ? 1.6 : 2, py: 1, cursor: "pointer", userSelect: "none", "&:hover": { bgcolor: TOKEN.archivedBg } }}
      >
        <InventoryIcon sx={{ fontSize: 14, color: TOKEN.archived }} />
        <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 800, color: "#475569", letterSpacing: 0.6, textTransform: "uppercase" }}>
          Archived / {leadConfig.supportsEventFields ? "Same Events" : "Duplicates"}
        </Typography>
        <Box sx={{ bgcolor: TOKEN.archivedBg, color: "#475569", fontSize: 10, fontWeight: 700, borderRadius: 10, px: 0.8, py: 0.1 }}>
          {archived.length}
        </Box>
        <Box flex={1} />
        {open ? <ExpandLessIcon sx={{ fontSize: 18, color: "#94a3b8" }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: "#94a3b8" }} />}
      </Stack>
      <Collapse in={open}>
        <Box sx={{ px: compact ? 1.6 : 2, pb: 1.3 }}>
          {sameEvent.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontSize: 9.5, fontWeight: 700, color: TOKEN.related, letterSpacing: 0.6, textTransform: "uppercase", mb: 0.5, display: "block" }}>
                {leadConfig.supportsEventFields ? "Same Event" : "Duplicates"} ({sameEvent.length})
              </Typography>
              <Stack gap={0.5} mb={otherArchived.length ? 1.2 : 0}>
                {sameEvent.map((l) => (
                  <ArchivedRow
                    key={l.id} lead={l} currentLead={currentLead} canManage={canManage}
                    onClick={() => onNavigate(l.id)}
                    onRestore={() => onRestore(l.id)}
                  />
                ))}
              </Stack>
            </>
          )}
          {otherArchived.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontSize: 9.5, fontWeight: 700, color: "#475569", letterSpacing: 0.6, textTransform: "uppercase", mb: 0.5, display: "block" }}>
                Archived ({otherArchived.length})
              </Typography>
              <Stack gap={0.5}>
                {otherArchived.map((l) => (
                  <ArchivedRow
                    key={l.id} lead={l} currentLead={currentLead} canManage={canManage}
                    onClick={() => onNavigate(l.id)}
                    onRestore={() => onRestore(l.id)}
                  />
                ))}
              </Stack>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

function ArchivedRow({
  lead, currentLead, canManage, onClick, onRestore,
}: {
  lead: LeadThread; currentLead: LeadThread; canManage: boolean;
  onClick: () => void; onRestore: () => void | Promise<void>;
}) {
  const resolution = (lead.resolution_status ?? "archived").toUpperCase().replace(/_/g, " ");
  const isCurrent = lead.id === currentLead.id;
  return (
    <Box
      onClick={onClick}
      sx={{
        p: 0.7, borderRadius: 1, cursor: "pointer",
        bgcolor: "#fff", border: `1px solid ${TOKEN.archivedEdge}`,
        opacity: 0.85,
        "&:hover": { opacity: 1, borderColor: TOKEN.archived },
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.5} flexWrap="wrap">
        <LeadNumberBadge value={lead.lead_number ?? `#${lead.id}`} variant="muted" />
        <Box sx={{ fontSize: 8.5, fontWeight: 800, letterSpacing: 0.4, bgcolor: TOKEN.archivedBg, color: "#475569", border: `1px solid ${TOKEN.archivedEdge}`, borderRadius: 0.6, px: 0.5, py: 0.05 }}>
          {resolution}
        </Box>
        {isCurrent && (
          <Box sx={{ fontSize: 8.5, fontWeight: 800, bgcolor: TOKEN.navy, color: "#fff", borderRadius: 0.6, px: 0.5, py: 0.05 }}>You</Box>
        )}
        <Typography variant="caption" sx={{ fontSize: 10.5, color: "#475569", textTransform: "capitalize", ml: 0.3 }}>
          {(() => {
            const s = resolveInquirySubject(lead);
            const bits: string[] = [];
            if (leadConfig.supportsEventFields) {
              bits.push(lead.event_type?.replace(/_/g, " ") ?? "—");
              if (showsVenueField("event_date")) bits.push(lead.event_date ?? "no date");
            } else if (s.program) {
              bits.push(s.program);
            }
            return bits.join(" · ") || "—";
          })()}
        </Typography>
        <Box flex={1} />
        <Tooltip title={fmtAbsolute(lead.archived_at ?? lead.updated_at ?? lead.created_at)}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9.5 }}>
            {fmtRelative(lead.archived_at ?? lead.updated_at ?? lead.created_at)}
          </Typography>
        </Tooltip>
        {canManage && (
          <CardAction
            label="Restore" color={TOKEN.success} bg={TOKEN.successBg} edge={TOKEN.successEdge}
            icon={<RestoreIcon sx={{ fontSize: 12 }} />}
            onClick={(e) => { e.stopPropagation(); onRestore(); }}
          />
        )}
      </Stack>
    </Box>
  );
}

/* ── Compact card-action button, used by every card.
   When `fullWidth` is set the button takes flex:1 so multiple actions
   in a row share the available width evenly. Label always stays on
   one line — buttons squeeze rather than wrap. */
function CardAction({
  icon, label, color, bg, edge, onClick, fullWidth,
}: {
  icon: React.ReactNode; label: string; color: string; bg: string; edge: string;
  fullWidth?: boolean;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <Button
      size="small" variant="outlined"
      startIcon={icon}
      onClick={onClick}
      sx={{
        textTransform: "none", fontWeight: 700, fontSize: 10.5,
        py: 0.25, px: 0.75, minWidth: 0, borderRadius: 1,
        lineHeight: 1.4, whiteSpace: "nowrap",
        ...(fullWidth ? { flex: 1, minWidth: 0 } : {}),
        color, bgcolor: "#fff",
        borderColor: edge,
        "& .MuiButton-startIcon": { mr: 0.4, ml: 0 },
        "& .MuiButton-startIcon > *:nth-of-type(1)": { fontSize: 13 },
        "&:hover": { bgcolor: bg, borderColor: color, boxShadow: `0 1px 6px ${color}22` },
      }}
    >
      {label}
    </Button>
  );
}

/* ═════ Formatters ═════════════════════════════════════════════════════ */

function fmtEventDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  if (Number.isNaN(d.getTime())) return iso as string;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtAbsolute(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso as string;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true });
}

function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso as string;
  const diff = Date.now() - t;
  const abs  = Math.abs(diff);
  const min  = Math.round(abs / 60_000);
  if (min < 1)  return diff >= 0 ? "just now" : "in a moment";
  if (min < 60) return diff >= 0 ? `${min}m ago` : `in ${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24)  return diff >= 0 ? `${hr}h ago` : `in ${hr}h`;
  const d = Math.round(hr / 24);
  if (d < 30)   return diff >= 0 ? `${d}d ago` : `in ${d}d`;
  const mo = Math.round(d / 30);
  if (mo < 12)  return diff >= 0 ? `${mo}mo ago` : `in ${mo}mo`;
  const yr = Math.round(mo / 12);
  return diff >= 0 ? `${yr}y ago` : `in ${yr}y`;
}

function displayName(u: any): string {
  if (!u) return "Unassigned";
  return (
    u.display_name ||
    [u.first_name, u.last_name].filter(Boolean).join(" ") ||
    u.username ||
    "Unassigned"
  );
}

function buildMatchReasons(lead: LeadThread, current: LeadThread): string[] {
  const out: string[] = [];
  if (lead.customer_id && current.customer_id && lead.customer_id === current.customer_id) out.push("Same customer");
  if (lead.phone && current.phone && lead.phone === current.phone) out.push("Same phone");
  if (lead.email && current.email && lead.email === current.email) out.push("Same email");
  // Event-date / event-type similarity is meaningful only for the venue
  // vertical. On autism / tutoring the corresponding columns are dormant
  // and any coincidental match would be misleading.
  if (leadConfig.supportsEventFields) {
    if (lead.event_date && current.event_date && lead.event_date === current.event_date) out.push("Same event date");
    if (lead.event_type && current.event_type && lead.event_type === current.event_type) out.push("Same event type");
  } else {
    const a = resolveInquirySubject(lead);
    const b = resolveInquirySubject(current);
    if (a.program && b.program && a.program.toLowerCase() === b.program.toLowerCase()) out.push("Same program");
    if (a.location && b.location && a.location.toLowerCase() === b.location.toLowerCase()) out.push("Same location");
  }
  return Array.from(new Set(out)).slice(0, 3);
}
